import React, { useMemo } from 'react';
import type { ColDef, ValueGetterParams } from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import { getSpecialRatesEffective } from '../../api/specialRates';
import { usePriceTypes } from '../../hooks/usePriceTypes';
import type { SpecialRateEffective } from '../../types/SpecialRate';
import { dateColumnProps } from '../../utils/dateColumn';
import { t } from '../../i18n/t';

// Read-only view: effective special rates merge tenant overrides over central
// defaults. No add/edit/delete — saveBatch is a no-op that returns the rows.
const noopSaveBatch = (
  _added: SpecialRateEffective[],
  _modified: SpecialRateEffective[],
  _deletedIds: unknown[],
): Promise<SpecialRateEffective[]> => Promise.resolve([]);

export function SpecialRatesEffectivePage() {
  const {
    data: priceTypes,
    loading: priceTypesLoading,
    error: priceTypesError,
  } = usePriceTypes();

  const columnDefs = useMemo<ColDef<SpecialRateEffective>[]>(() => {
    if (!priceTypes) return [];

    const priceTypeLabelById = new Map<number, string>(
      priceTypes.map((p) => [
        p.priceTypeId,
        `${p.priceTypeId} - ${p.priceTypeName}`,
      ]),
    );

    return [
      {
        field: 'assetId',
        headerName: t('specialRates.columns.assetId'),
        width: 120,
        editable: false,
        cellDataType: 'number',
        sort: 'asc',
      },
      {
        field: 'dDate',
        headerName: t('specialRates.columns.dDate'),
        width: 160,
        editable: false,
        ...dateColumnProps<SpecialRateEffective>('dDate'),
      },
      {
        field: 'rate',
        headerName: t('specialRates.columns.rate'),
        width: 130,
        editable: false,
        cellDataType: 'number',
      },
      {
        field: 'priceTypeId',
        headerName: t('specialRates.columns.priceTypeId'),
        width: 200,
        editable: false,
        valueGetter: (p: ValueGetterParams<SpecialRateEffective>) => {
          if (!p.data) return '';
          return (
            priceTypeLabelById.get(p.data.priceTypeId) ??
            String(p.data.priceTypeId)
          );
        },
      },
      {
        field: 'manualRateComment',
        headerName: t('specialRates.columns.manualRateComment'),
        flex: 1,
        editable: false,
      },
      {
        field: 'isUpdateOnlineToo',
        headerName: t('specialRates.columns.isUpdateOnlineToo'),
        width: 130,
        editable: false,
        cellDataType: 'boolean',
      },
      {
        field: 'rateSource',
        headerName: t('specialRates.columns.rateSource'),
        width: 110,
        editable: false,
        valueGetter: (p: ValueGetterParams<SpecialRateEffective>) => {
          if (!p.data) return '';
          return p.data.rateSource === 'central'
            ? t('specialRates.source.central')
            : t('specialRates.source.tenant');
        },
      },
    ];
  }, [priceTypes]);

  if (priceTypesError) {
    return (
      <div style={{ padding: 16 }}>
        <div
          style={{
            color: 'var(--color-error-text)',
            background: 'var(--color-error-bg)',
            padding: '8px 12px',
            borderRadius: 4,
          }}
        >
          {t('specialRates.errorPrefix', { message: priceTypesError })}
        </div>
      </div>
    );
  }

  if (priceTypesLoading || !priceTypes) {
    return (
      <div style={{ padding: 16, color: 'var(--color-text-secondary)' }}>{t('common.loading')}</div>
    );
  }

  return (
    <LopCrudPage<SpecialRateEffective>
      resourceCode="specialRatesEffective"
      titleKey="specialRates.effectivePageTitle"
      idField="assetId"
      getRows={getSpecialRatesEffective}
      saveBatch={noopSaveBatch}
      columnDefs={columnDefs}
      showAdd={false}
      showDelete={false}
    />
  );
}
